// src/pages/Home/ItemSubcategory.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import config from '../../config';

const UNITS = ['ml', 'ltr', 'gm', 'kg', 'pcs', 'nos'];
const empty = {
  parentItemId: '',
  subcategoryName: '',
  consumptionQty: '',
  consumptionUnit: 'ml',
  sellingPrice: '',
  activeStatus: true,
};

export default function ItemSubcategoryPage() {
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const token = sessionStorage.getItem('token');

  const [allItems, setAllItems] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [form, setForm] = useState(empty);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [filterParent, setFilterParent] = useState('');

  const fetchItems = async () => {
    const res = await axios.get(
      `${config.API_BASE_URL}/Home/GetallItemsAsync/${company.id}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    setAllItems(res.data || []);
  };

  const fetchSubs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/home/GetSubcategories/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSubcategories(res.data || []);
    } catch {
      setAlert({ type: 'error', msg: 'Failed to load' });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
    fetchSubs();
  }, []);

  const save = async () => {
    if (!form.parentItemId || !form.subcategoryName || !form.sellingPrice) {
      return setAlert({
        type: 'warning',
        msg: 'Parent Item, Name and Selling Price required',
      });
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        companyId: company.id,
        parentItemId: parseInt(form.parentItemId),
        consumptionQty: parseFloat(form.consumptionQty) || 0,
        sellingPrice: parseFloat(form.sellingPrice),
      };
      if (form.id)
        await axios.post(
          `${config.API_BASE_URL}/home/UpdateSubcategory`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      else
        await axios.post(
          `${config.API_BASE_URL}/home/AddSubcategory`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      setAlert({ type: 'success', msg: 'Saved!' });
      setForm(empty);
      setEditOpen(false);
      fetchSubs();
    } catch {
      setAlert({ type: 'error', msg: 'Failed to save' });
    }
    setSaving(false);
  };

  const toggle = async (id) => {
    await axios.get(`${config.API_BASE_URL}/home/ToggleSubcategory/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchSubs();
  };

  const filtered = subcategories.filter(
    (x) => !filterParent || String(x.parentItemId) === String(filterParent),
  );

  return (
    <Container maxWidth='lg' sx={{ mt: 4, mb: 6 }}>
      {alert && (
        <Alert
          severity={alert.type}
          onClose={() => setAlert(null)}
          sx={{ mb: 2 }}>
          {alert.msg}
        </Alert>
      )}

      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 4 }}>
        <CardContent>
          <Typography variant='h6' fontWeight={700} color='primary' mb={2}>
            Add Item Variant / Subcategory
          </Typography>
          <Typography variant='body2' color='text.secondary' mb={2}>
            Example: Parent = "Signature Bottle" → Variants: 30ml ₹200, 60ml
            ₹300, 90ml ₹450
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box display='flex' flexWrap='wrap' gap={2}>
            <FormControl size='small' sx={{ minWidth: 220 }}>
              <InputLabel>Parent Item *</InputLabel>
              <Select
                label='Parent Item *'
                value={form.parentItemId}
                onChange={(e) =>
                  setForm({ ...form, parentItemId: e.target.value })
                }>
                {allItems.map((i) => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label='Variant Name *'
              size='small'
              value={form.subcategoryName}
              onChange={(e) =>
                setForm({ ...form, subcategoryName: e.target.value })
              }
              placeholder='e.g. 30ml Peg'
              sx={{ minWidth: 180 }}
            />
            <TextField
              label='Consumption Qty'
              size='small'
              type='number'
              value={form.consumptionQty}
              onChange={(e) =>
                setForm({ ...form, consumptionQty: e.target.value })
              }
              sx={{ minWidth: 140 }}
            />
            <TextField
              select
              label='Unit'
              size='small'
              value={form.consumptionUnit}
              onChange={(e) =>
                setForm({ ...form, consumptionUnit: e.target.value })
              }
              sx={{ minWidth: 100 }}
              SelectProps={{ native: true }}>
              {UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </TextField>
            <TextField
              label='Selling Price *'
              size='small'
              type='number'
              value={form.sellingPrice}
              onChange={(e) =>
                setForm({ ...form, sellingPrice: e.target.value })
              }
              sx={{ minWidth: 140 }}
            />
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={save}
              disabled={saving}>
              {saving ? 'Saving...' : 'Add Variant'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
        <CardContent>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mb={2}>
            <Typography variant='h6' fontWeight={600}>
              All Variants
            </Typography>
            <FormControl size='small' sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Parent</InputLabel>
              <Select
                label='Filter by Parent'
                value={filterParent}
                onChange={(e) => setFilterParent(e.target.value)}>
                <MenuItem value=''>All</MenuItem>
                {allItems.map((i) => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {loading ? (
            <Box textAlign='center' py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {[
                      '#',
                      'Parent Item',
                      'Variant Name',
                      'Consumes',
                      'Selling Price',
                      'Status',
                      'Actions',
                    ].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((sub, i) => (
                    <TableRow key={sub.id} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <Chip
                          label={sub.parentItemName}
                          size='small'
                          color='primary'
                          variant='outlined'
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {sub.subcategoryName}
                      </TableCell>
                      <TableCell>
                        {sub.consumptionQty} {sub.consumptionUnit}
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: 'success.main' }}>
                        ₹{sub.sellingPrice}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sub.activeStatus ? 'Active' : 'Inactive'}
                          color={sub.activeStatus ? 'success' : 'error'}
                          size='small'
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size='small'
                          variant='contained'
                          sx={{ mr: 1 }}
                          onClick={() => {
                            setForm({ ...sub });
                            setEditOpen(true);
                          }}>
                          <EditIcon sx={{ fontSize: 14 }} />
                        </Button>
                        <Button
                          size='small'
                          variant='outlined'
                          color={sub.activeStatus ? 'warning' : 'success'}
                          onClick={() => toggle(sub.id)}>
                          {sub.activeStatus ? 'Disable' : 'Enable'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle fontWeight={700}>Edit Variant</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label='Variant Name'
            value={form.subcategoryName}
            onChange={(e) =>
              setForm({ ...form, subcategoryName: e.target.value })
            }
            fullWidth
          />
          <TextField
            label='Consumption Qty'
            type='number'
            value={form.consumptionQty}
            onChange={(e) =>
              setForm({ ...form, consumptionQty: e.target.value })
            }
            fullWidth
          />
          <TextField
            select
            label='Unit'
            value={form.consumptionUnit}
            onChange={(e) =>
              setForm({ ...form, consumptionUnit: e.target.value })
            }
            fullWidth
            SelectProps={{ native: true }}>
            {UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </TextField>
          <TextField
            label='Selling Price'
            type='number'
            value={form.sellingPrice}
            onChange={(e) => setForm({ ...form, sellingPrice: e.target.value })}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={save} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
